<script lang="ts" setup>
  import { vMaska, type MaskOptions } from 'maska'
  import type { InputMode } from '@/shared/types/common.types'
  import { reactive, computed } from 'vue'

  interface Props {
    value: string
    label: string
    disabled?: boolean
    inputMode?: InputMode
    clearable?: boolean
    focused?: boolean
    big?: boolean
    errors: string[]
  }
  interface Emits {
    (e: 'update:modelValue', rangeDate: string): void
    (e: 'clear'): void
    (e: 'focus'): void
    (e: 'input', event: Event): void
    (e: 'change', event: Event): void
  }

  const props = withDefaults(defineProps<Props>(), {
    locale: 'en',
    inputMode: 'numeric',
    disabled: false,
    focused: false,
    big: false,
    errors: () => [],
  })
  const emit = defineEmits<Emits>()

  const FORMAT_DATE = 'dd/MM/yyyy'
  const inputMask = FORMAT_DATE.replace(/[A-Za-z]/g, '#')
  const maskConfig: MaskOptions = { mask: inputMask }

  const maskOptions = reactive(maskConfig)

  const onInput = (event: Event) => {
    emit('input', event)
  }

  const onInputFocus = () => {
    emit('focus')
  }

  const hasErrors = computed(() => props.errors.length > 0)
  const inputInvalid = computed(() => hasErrors.value && !props.focused)
</script>

<template>
  <div
    class="input-container"
    :class="{ 'input-container_big': big, 'input-container_invalid': inputInvalid }"
  >
    <!-- @vue-ignore -->
    <input
      v-on="$attrs"
      :value="props.value"
      type="text"
      class="input"
      :class="{ input_big: big, focus: focused }"
      :disabled="disabled"
      placeholder=" "
      :inputmode="inputMode"
      @input="onInput"
      @focus="onInputFocus"
      @change="emit('change', $event)"
      v-maska:[maskOptions]
    />
    <label class="label">{{ label }}</label>
  </div>
</template>

<style scoped lang="scss">
  // TODO: as is: styles globally imported from qic-lib. Need to add some events to BaseInput to use it here
  .input {
    padding-inline: 20px;

    &.focus {
      border: 1px solid var(--input-actions-border);
      box-shadow: 0 0 0 4px var(--focus-shadow);
      background-color: var(--input-bg-active);
      background-position: right 20px center;
    }

    &:disabled {
      -webkit-text-fill-color: #1c1c1c;
      color: #1c1c1c;
      opacity: 1;
      cursor: pointer;
    }
  }

  .input-container_big {
    max-width: 100%;
  }

  .outbound .input {
    &.focus {
      border: 1px solid var(--colorBgSrfAccentBrand);
    }
  }
</style>
